// /apps/patient/src/pages/forms/FormResponseDetail.tsx

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/layout/PageHeader';
import { Loader2 } from 'lucide-react';
import { patientApiService as apiService } from '../../services/api';
import { FormResponse, FormDefinition, Question, QuestionAnswer } from '@prism-ehr/shared/types';
import { FormSection } from '@prism-ehr/shared/types';
import { formatDateAlt } from '@prism-ehr/shared/utils';

interface GroupedSection {
  sectionId: string;
  sectionTitle: string;
  questions: QuestionAnswer[];
}

export function FormResponseDetail(){
  const { id } = useParams<{ id: string }>();
  const [response, setResponse] = useState<FormResponse | null>(null);
  const [formDefinition, setFormDefinition] = useState<FormDefinition | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [groupBySection, setGroupBySection] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchResponseAndForm = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch the response
        const responseData = await apiService.getFormResponse(id);
        setResponse(responseData);
        
        // Fetch the form definition from its version
        const formData = await apiService.getFormDefinition(responseData.form_id);
        if (formData.form_version?.definition) {
          setFormDefinition(formData.form_version.definition);
        }
        
      } catch (err) {
        console.error('Failed to fetch response details:', err);
        setError('Failed to load response details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchResponseAndForm();
  }, [id]);

  // Helper function to get patient name
  const getPatientName = (): string => {
    if (!response) return '';
    
    let firstName = '';
    let lastName = '';
    
    // Search through question_answers for name fields
    for (const qa of response.question_answers) {
      if (qa.was_visible !== false) {
        if (!firstName && 
            (qa.question_text.toLowerCase().includes('first name') || 
             qa.question_text.toLowerCase() === 'first' ||
             qa.question_text.toLowerCase() === 'given name')) {
          firstName = qa.answer_text || '';
        }
        
        if (!lastName && 
            (qa.question_text.toLowerCase().includes('last name') || 
             qa.question_text.toLowerCase() === 'last' || 
             qa.question_text.toLowerCase() === 'surname' || 
             qa.question_text.toLowerCase() === 'family name')) {
          lastName = qa.answer_text || '';
        }
        
        // Some forms might have a single field for full name
        if (!firstName && !lastName && 
            (qa.question_text.toLowerCase() === 'name' || 
             qa.question_text.toLowerCase() === 'full name' ||
             qa.question_text.toLowerCase() === 'patient name')) {
          return qa.answer_text || '';
        }
      }
    }
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    }
    
    // Fallback to response ID if no name is found
    return `Response ${response.id.substring(0, 8)}`;
  };

  // Filter and sort questions by section and order
  const getQuestionsGroupedBySections = (): GroupedSection[] => {
    if (!response || !formDefinition) return [];
    
    // Filter for only visible questions
    const visibleQuestions = response.question_answers.filter(qa => qa.was_visible !== false);
    
    // Get ordered sections from form definition
    const orderedSections = [...(formDefinition.sections || [])].sort((a: FormSection, b: FormSection) => a.order - b.order);
    
    if (groupBySection) {
      // Group questions by section
      return orderedSections.map((section: FormSection) => {
        const sectionQuestions = visibleQuestions.filter(qa => qa.section_id === section.id);
        
        // Skip empty sections
        if (sectionQuestions.length === 0) return null;
        
        // Sort questions by their order in the form
        const sortedQuestions = [...sectionQuestions].sort((a: QuestionAnswer, b: QuestionAnswer) => {
          const questionA = formDefinition.questions?.find((q: Question) => q.id === a.question_id);
          const questionB = formDefinition.questions?.find((q: Question) => q.id === b.question_id);
          return (questionA?.order || 0) - (questionB?.order || 0);
        });
        
        return {
          sectionId: section.id,
          sectionTitle: section.title,
          questions: sortedQuestions
        };
      }).filter(Boolean) as GroupedSection[]; // Remove null entries (empty sections)
    } else {
      // Flatten all questions into a single section
      return [{
        sectionId: 'all',
        sectionTitle: 'All Questions',
        questions: visibleQuestions.sort((a: QuestionAnswer, b: QuestionAnswer) => {
          const sectionA = formDefinition.sections?.find((s: FormSection) => s.id === a.section_id);
          const sectionB = formDefinition.sections?.find((s: FormSection) => s.id === b.section_id);
          const sectionOrderDiff = (sectionA?.order || 0) - (sectionB?.order || 0);
          
          if (sectionOrderDiff !== 0) return sectionOrderDiff;
          
          const questionA = formDefinition.questions?.find((q: Question) => q.id === a.question_id);
          const questionB = formDefinition.questions?.find((q: Question) => q.id === b.question_id);
          return (questionA?.order || 0) - (questionB?.order || 0);
        })
      }];
    }
  };

  // Helper function to render a question's answer - Fixed to handle different answer_text types
  const renderAnswer = (questionAnswer: QuestionAnswer) => {
    if (!questionAnswer.answer && questionAnswer.answer !== 0) {
      return <span className="text-gray-500">No answer provided</span>;
    }
    
    // Handle different types of answers
    if (Array.isArray(questionAnswer.answer)) {
      return (
        <ul className="list-disc pl-5">
          {questionAnswer.answer.map((item: any, index: number) => (
            <li key={index} className="mb-1">
              {typeof item === 'object' ? JSON.stringify(item) : String(item)}
            </li>
          ))}
        </ul>
      );
    } else if (typeof questionAnswer.answer === 'object') {
      // Format JSON nicely
      return <pre className="bg-gray-50 p-2 rounded text-sm overflow-auto">{JSON.stringify(questionAnswer.answer, null, 2)}</pre>;
    } else if (
      typeof questionAnswer.answer_text === 'string' && 
      questionAnswer.answer_text.startsWith('data:image')
    ) {
      // Handle signature or image data
      return <img src={questionAnswer.answer_text} alt="Signature" className="max-w-xs h-auto" />;
    } else {
      // Simple text or number
      return <span>{
        typeof questionAnswer.answer_text === 'string' 
          ? questionAnswer.answer_text 
          : String(questionAnswer.answer)
      }</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !response) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
        <p className="text-red-700">{error || 'Response not found'}</p>
      </div>
    );
  }

  const groupedSections = getQuestionsGroupedBySections();

  return (
    <div className="min-w-0 overflow-hidden">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <PageHeader title={`${getPatientName()}`} />
        </div>
        
        {/* Response meta information */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500">Date Received</p>
              <p className="font-medium">{formatDateAlt(response.created_at)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <div className="flex items-center">
                <span 
                  className={`px-2 py-1 text-xs rounded-full ${
                    response.status === 'complete' 
                      ? 'bg-green-100 text-green-800' 
                      : response.status === 'partial'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {response.status.charAt(0).toUpperCase() + response.status.slice(1)}
                </span>
              </div>
            </div>
            {response.completion_time_seconds !== undefined && (
              <div>
                <p className="text-sm text-gray-500">Completion Time</p>
                <p className="font-medium">{Math.floor(response.completion_time_seconds / 60)} min {response.completion_time_seconds % 60} sec</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Section toggle */}
        <div className="flex items-center justify-end mb-6">
          <span className="text-sm text-gray-600 mr-2">Group by Section</span>
          <button
            onClick={() => setGroupBySection(prev => !prev)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
              groupBySection ? 'bg-primary' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                groupBySection ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        
        {/* Questions and answers by section */}
        <div className="space-y-8">
          {groupedSections.map((section: GroupedSection) => (
            <div key={section.sectionId} className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-6 pb-2 border-b">
                {section.sectionTitle}
              </h2>
              
              <div className="space-y-6">
                {section.questions.map((question: QuestionAnswer) => (
                  <div key={question.question_id} className="border-b pb-4 last:border-0">
                    <p className="text-sm text-gray-500 mb-1">{question.question_text}</p>
                    <div className="font-medium">
                      {renderAnswer(question)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
